import {TaxProfileField} from 'src/store/taxProfile/taxProfile.types';
import {
  FORM_INPUT_TYPE,
  FormConfigProps,
} from 'src/DesignSystem/Form/Form.types';
import {VALIDATOR} from 'src/DesignSystem/Form/Form.validators';
import {ReturnFields} from './CreateReturn.types';
import {FILING_STATUS_ANSWER} from 'src/store/taxProfile/taxProfile.types';
import {
  BusinessDetailsField,
  BusinessType,
} from 'src/store/businessDetails/businessDetails.types';
import _ from 'lodash';
import {PartnershipReturnFields} from 'src/appApi.types';
import {TRANSFORMERS} from 'src/DesignSystem/Form/Forms.transformers';

const previousYear = new Date().getFullYear() - 1;
export const PARTNERSHIP_RETURNS_SUPPORTED_YEARS: number[] = [];

for (var year = previousYear; year > previousYear - 5; year--) {
  PARTNERSHIP_RETURNS_SUPPORTED_YEARS.push(year);
}

export const TaxReturnBasicFieldsConfig: FormConfigProps[] = [
  {
    title: 'First name & middle initial',
    path: TaxProfileField.FIRST_NAME,
    inputType: FORM_INPUT_TYPE.String,
    validators: [VALIDATOR.NON_EMPTY_STRING, VALIDATOR.NON_NULL_VALUE],
  },
  {
    title: 'Last name',
    path: TaxProfileField.LAST_NAME,
    inputType: FORM_INPUT_TYPE.String,
    validators: [VALIDATOR.NON_EMPTY_STRING, VALIDATOR.NON_NULL_VALUE],
  },
  {
    title: 'SSN',
    path: TaxProfileField.SSN,
    inputType: FORM_INPUT_TYPE.String,
    validators: [VALIDATOR.VALIDATE_SSN, VALIDATOR.NON_NULL_VALUE],
    transformers: [TRANSFORMERS.SSN],
  },
  {
    title: 'Partner',
    path: ReturnFields.partner,
    inputType: FORM_INPUT_TYPE.SingleSelect,
    validators: [VALIDATOR.NON_NULL_VALUE],
    options: [],
  },
  {
    title: 'Tax year',
    path: ReturnFields.years,
    inputType: FORM_INPUT_TYPE.MultiSelect,
    options: PARTNERSHIP_RETURNS_SUPPORTED_YEARS.map((year) => ({
      label: year,
      value: year,
    })),
    validators: [VALIDATOR.NON_EMPTY_ARRAY, VALIDATOR.NON_NULL_VALUE],
  },
];

export const IndividualReturnFieldsConfig: FormConfigProps[] = [
  {
    title: 'Filing status',
    path: TaxProfileField.FILING_STATUS,
    inputType: FORM_INPUT_TYPE.SingleSelect,
    validators: [VALIDATOR.NON_EMPTY_STRING, VALIDATOR.NON_NULL_VALUE],
    options: Object.values(FILING_STATUS_ANSWER).map((status) => ({
      label: status,
      value: status,
    })),
  },
  {
    title: 'Spouse SSN',
    path: TaxProfileField.SPOUSE_SSN,
    inputType: FORM_INPUT_TYPE.String,
    validators: [VALIDATOR.VALIDATE_SSN, VALIDATOR.NON_NULL_VALUE],
  },
  {
    title: 'Filing',
    path: ReturnFields.should_file_individual_return,
    inputType: FORM_INPUT_TYPE.Boolean,
  },
];

export const BusinessReturnFieldsConfig: FormConfigProps[] = [
  {
    title: 'Filing',
    path: ReturnFields.should_file_return,
    inputType: FORM_INPUT_TYPE.Boolean,
  },
  {
    title: 'Business name',
    path: BusinessDetailsField.name,
    inputType: FORM_INPUT_TYPE.String,
    validators: [VALIDATOR.NON_EMPTY_STRING, VALIDATOR.NON_NULL_VALUE],
  },
  {
    title: 'Business entity',
    path: BusinessDetailsField.entity_type,
    inputType: FORM_INPUT_TYPE.SingleSelect,
    validators: [VALIDATOR.NON_EMPTY_STRING, VALIDATOR.NON_NULL_VALUE],
    options: [
      {value: BusinessType.PARTNERSHIP, label: 'Partnership'},
      {value: BusinessType.S_CORP, label: 'S-corp'},
      {value: BusinessType.C_CORP, label: 'C-crop'},
    ],
  },
  {
    title: 'Business EIN',
    path: BusinessDetailsField.ein,
    inputType: FORM_INPUT_TYPE.String,
    validators: [VALIDATOR.VALIDATE_EIN, VALIDATOR.NON_NULL_VALUE],
    transformers: TRANSFORMERS.EIN,
  },
];

export const ManualReturnFieldsConfig: FormConfigProps[] = [
  ...TaxReturnBasicFieldsConfig,
  ...IndividualReturnFieldsConfig,
  {
    title: 'Businesses',
    path: ReturnFields.businesses,
    inputType: FORM_INPUT_TYPE.Array,
    childProps: BusinessReturnFieldsConfig,
    childTitle: 'Business',
  },
];

export const UsersRelationFieldConfig: FormConfigProps[] = [
  {
    title: 'Users',
    path: ReturnFields.users,
    inputType: FORM_INPUT_TYPE.Array,
    childProps: [
      {
        title: 'First name & middle initial',
        path: TaxProfileField.FIRST_NAME,
        inputType: FORM_INPUT_TYPE.String,
        validators: [VALIDATOR.NON_EMPTY_STRING, VALIDATOR.NON_NULL_VALUE],
      },
      {
        title: 'Last name',
        path: TaxProfileField.LAST_NAME,
        inputType: FORM_INPUT_TYPE.String,
        validators: [VALIDATOR.NON_EMPTY_STRING, VALIDATOR.NON_NULL_VALUE],
      },
      {
        title: 'SSN',
        path: TaxProfileField.SSN,
        inputType: FORM_INPUT_TYPE.String,
        validators: [VALIDATOR.VALIDATE_SSN, VALIDATOR.NON_NULL_VALUE],
      },
      {
        title: 'One of the users is spouse of this user',
        inputType: FORM_INPUT_TYPE.Boolean,
        path: ReturnFields.has_spouse,
      },
      {
        title: 'Select spouse',
        inputType: FORM_INPUT_TYPE.SingleSelect,
        options: [],
        path: ReturnFields.spouse_id,
        allowClear: true,
      },
    ],
    childTitle: 'User',
  },
];

export const MultipleUsersFieldConfig: FormConfigProps[] = [
  {
    title: 'Users',
    path: ReturnFields.users,
    inputType: FORM_INPUT_TYPE.Array,
    childProps: _.cloneDeep(ManualReturnFieldsConfig),
    childTitle: 'User',
  },
];

export const SingleDocumentAssignmentConfig: FormConfigProps[] = [
  {
    title: 'Select User',
    path: PartnershipReturnFields.fly_user_id,
    inputType: FORM_INPUT_TYPE.SingleSelect,
    options: [],
    validators: [VALIDATOR.NON_NULL_VALUE],
  },
  {
    title: 'Years',
    path: ReturnFields.years,
    inputType: FORM_INPUT_TYPE.MultiSelect,
    options: [],
    validators: [VALIDATOR.NON_NULL_VALUE, VALIDATOR.NON_EMPTY_ARRAY],
  },
  {
    title: 'This belongs to spouse',
    path: ReturnFields.belongs_to_spouse,
    inputType: FORM_INPUT_TYPE.Boolean,
  },
];

export const MultipleDocumentAssignmentConfig: FormConfigProps[] = [
  {
    title: 'Assign Documents',
    path: ReturnFields.documents,
    inputType: FORM_INPUT_TYPE.Array,
    childTitle: 'Document',
    childProps: SingleDocumentAssignmentConfig,
  },
];
